/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { DragHandle } from '@/components/ui/configurable-card/ConfigurableCard'
import { useDashboard } from '@/hooks'

export interface UnavailableInsightCardProps {
  id: string
  title: string
  /** not-allowed: the account no longer includes it; unregistered: the insight was retired. */
  reason: 'not-allowed' | 'unregistered'
  editMode?: boolean
}

const REASON_TEXT: Record<UnavailableInsightCardProps['reason'], string> = {
  'not-allowed': 'Your account does not include this insight.',
  unregistered: 'This insight has been retired.',
}

/**
 * Stands in for an insight the user can no longer use: same footprint as a real card so
 * the layout holds, but none of the configure/refresh/export/fullscreen controls.
 */
export const UnavailableInsightCard: React.FC<UnavailableInsightCardProps> = ({ id, title, reason, editMode }) => {
  const { removeInsight } = useDashboard()
  return (
    <section
      id={id}
      className="flex h-full w-full flex-col rounded-lg bg-base-100 shadow"
      data-testid="unavailable-insight"
    >
      <div className="flex items-center px-4 py-2">
        {editMode && <DragHandle />}
        <h3 className="truncate text-sm font-semibold text-base-content/70">{title}</h3>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="font-medium">{title} is no longer available</p>
        <p className="text-sm text-base-content/60">{REASON_TEXT[reason]}</p>
        <button type="button" className="btn btn-ghost btn-sm mt-2" onClick={() => removeInsight({ id })}>
          Remove
        </button>
      </div>
    </section>
  )
}

export default UnavailableInsightCard
